import { route } from "../../urls";
import Authentication from "./authentication.mdx";
import { MDXSection } from "./snippets";
import { StatusDiagram } from "./status-diagram";

## Authentication

<Authentication token={props.token} />

## Define the inputs

First, you define the input for the model in JSON. There is no great way to do this in bash. These examples will use the [heredoc](https://itslinuxfoss.com/bash-heredoc-explained/) syntax.

<MDXSection show={props.usesVersionlessApi}>
```bash
input=$(
  cat <<-EOM
{
  "input": {{ input }}
}
EOM
)
```
</MDXSection>
<MDXSection show={!props.usesVersionlessApi}>
```bash
input=$(
  cat <<-EOM
{
  "{{ runnableFieldName }}": "{{ runnableFieldValue }}",
  "input": {{ input }}
}
EOM
)
```
</MDXSection>

<MDXSection show={props.hasFileInputs}>

## File inputs

This model expects a file, named { props.fileInputField }, as part of its inputs. There are three ways to provide files to Replicate.

### Option 1: Hosted file

Use a URL:

```bash
{{ fileInputField }}="{{ fileInputValue }}"
```

This is useful if you already have an image hosted somewhere on the internet.

### Option 2: Local file

Replicate has an API to upload your file.

<aside>

<details>

<summary>What is jq?</summary>
<div className="details-inner">

The following examples make use of the [`jq`](https://jqlang.github.io/jq/) tool for working with the JSON data returned from the Replicate API. It takes a series of keys seperated by a period and returns the value at the end of that path.

For example, if we have a file called `my_file.json` with the following content:

```json
{ "result": { "message": "hello world" } }
```

We can use `jq` to extract the message value:

```shell
cat my_file.json | jq -r '.result.message' # => "hello world"
```

</div>
</details>

</aside>

```bash
{{ fileInputField }}="./{{ fileLocalValue }}"
file=$(
	curl -s -X POST "https://api.replicate.com/v1/files" \
		-H "Authorization: Bearer $REPLICATE_API_TOKEN" \
		-H "Content-Type: multipart/form-data" \
		-F "content=@$image;type=application/octet-stream;filename=${{ fileInputField }}"
)
{{ fileInputField }}_url=$(printf "%s" "$file" | jq -r .urls.get)
# "https://delivery.replicate.com/some/path/to/{{ fileLocalValue }}"
```

### Option 3: Data URI

Lastly, you can create a [data URI](https://en.wikipedia.org/wiki/Data_URI_scheme) consisting of the base64 encoded data for your file, but this is only recommended if the file is < 1mb

<aside>
<details>
<summary>Using `base64` from the command line</summary>
<div className="details-inner">

This example uses the `base64` command to generate the base64 encoded data for the image. This may not be available on your machine depending on the operating system and environment.

</div>
</details>
</aside>

```bash
{{ fileInputField }}_url="data:application/octet-stream,$(base64 < ./{{ fileLocalValue }})"
# "data:application/octet-stream,N4os7sCFSz4cyHDmRor1zLPgQ4D..."
```

Then pass the file as part of the input:

<MDXSection show={props.usesVersionlessApi}>
```bash
input=$(
  cat <<-EOM
{
  "input": {{ inputWithFileAsBashVariable }}
}
EOM
)
```
</MDXSection>
<MDXSection show={!props.usesVersionlessApi}>
```bash
input=$(
  cat <<-EOM
{
  "{{ runnableFieldName }}": "{{ runnableFieldValue }}",
  "input": {{ inputWithFileAsBashVariable }}
}
EOM
)
```
</MDXSection>

</MDXSection>

## Run the model

Use the `predictions.create` endpoint to run the model and create a prediction:

```bash
output=$(
	curl --silent --show-error {{ predictionApiEndpoint }} \
		--request POST \
		--header "Authorization: Bearer $REPLICATE_API_TOKEN" \
		--header "Content-Type: application/json" \
		--data "$input"
)
```

You can learn about pricing for this model on the <a href={route("model_detail", { name: props.version._extras.model.name, username: props.version._extras.model.owner })}>model page</a>.

The endpoint returns the initial prediction object that can then be used to fetch updates by looking at the `urls.get` field.

```bash
prediction_url=$(printf "%s" "$output" | jq -r .urls.get)
```

If you'd prefer to wait for the output, you can use the `Prefer: wait` header, which will return base64 encoded output in the response.

```bash
output=$(
	curl --silent --show-error {{ predictionApiEndpoint }} \
		--request POST \
		--header "Authorization: Bearer $REPLICATE_API_TOKEN" \
		--header "Content-Type: application/json" \
		--header "Prefer: wait" \
		--data "$input"
)
```

## Prediction lifecycle

Running predictions and trainings can often take significant time to complete, beyond what is reasonable for an HTTP request/response.

When you run a model on Replicate, the prediction is created with a `“starting”` state, then instantly returned. This will then move to `"processing"` and eventual one of `“successful”`, `"failed"` or `"canceled"`.

<StatusDiagram />

## Polling for updates

To get the latest state of a model poll the `predictions.get` endpoint to retrieve the latest status. This is handled for you when using the [client libraries](https://replicate.com/docs/reference/client-libraries).

<details>

<summary>Show example</summary>

<div className="details-inner">

Here a while loop is used that fetches the latest version from the  `predictions.get` endpoint and checks the `status` field to see if it has `“succeeded”` otherwise it waits two seconds before trying again.

```bash
prediction_url=$(printf "%s" "$output" | jq -r .urls.get)

while true; do
	output=$(curl -s -H "Authorization: Bearer $REPLICATE_API_TOKEN" $prediction_url)
	prediction_status=$(printf "%s" "$output" | jq -r .status)
	if [ "$prediction_status"=="succeeded" ]; then
		printf "%s" "$output" | jq -r '.output | join("")'
		break
	fi
	if [ "$prediction_status"=="failed" ]; then
		exit 1
	fi
	sleep 2
done
```
</div>

</details>

<MDXSection show={props.streaming !== "none"}>

## Streaming

This model also supports streaming.

To stream the output you need to extract the streaming URL from the `urls.stream` field in the response.

<details>

<summary>Show example</summary>

<div className="details-inner">

<MDXSection show={props.usesVersionlessApi}>
```bash
output=$(
	curl --silent --show-error {{ predictionApiEndpoint }} \
		--request POST \
		--header "Authorization: Bearer $REPLICATE_API_TOKEN" \
		--header "Content-Type: application/json" \
		--data @- <<'EOM'
{
	"input": {{ input }},
	"stream": true
}
EOM
)
# { "id": "xyz...", "status": "starting", ... }

stream_url=$(printf "%s" "$output" | jq -r .urls.stream)
```
</MDXSection>
<MDXSection show={!props.usesVersionlessApi}>
```bash
output=$(
	curl --silent --show-error {{ predictionApiEndpoint }} \
		--request POST \
		--header "Authorization: Bearer $REPLICATE_API_TOKEN" \
		--header "Content-Type: application/json" \
		--data @- <<'EOM'
{
	"{{ runnableFieldName }}": "{{ runnableFieldValue }}",
	"input": {{ input }},
	"stream": true
}
EOM
)
# { "id": "xyz...", "status": "starting", ... }

stream_url=$(printf "%s" "$output" | jq -r .urls.stream)
```
</MDXSection>

</div>
</details>

Then make a request to the stream to get a response in the [Server Sent Events](https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events/Using_server-sent_events) format.

```bash
curl -s --no-buffer "$stream_url" \
  -H "Accept: text/event-stream" \
  -H "Cache-Control: no-store"
# event: output
# data: {{ outputStreamEvent }}
```

## Streaming in the browser

The streaming URL uses a standard format called [Server Sent Events](https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events/Using_server-sent_events) (or `text/event-stream`) built into all web browsers.

A common implementation is to use a web server to create the prediction using `replicate.predictions.create`. The `urls.stream` property of the response contains a URL that you can send to your frontend application.

```js
const source = new EventSource(streamUrl);
source.addEventListener("output", (evt) => {
  console.log(evt.data) //=> {{ outputStreamEvent }}
});
source.addEventListener("done", (evt) => {
  console.log("stream is complete");
});
```

</MDXSection>

## Webhooks

Webhooks provide real-time updates about your prediction. Specify an endpoint when you [create a prediction](https://replicate.com/docs/reference/http#predictions.create), and Replicate will send HTTP POST requests to that URL when the prediction is created, updated, and finished.

It is possible to provide a URL to the `predictions.create()` function that will be requested by Replicate when the prediction status changes. This is an alternative to polling.

The client libraries provide clearer examples of running webhooks locally. However, read on if you do want to see a working example using command line tools.

<details>

<summary>Show example</summary>

<div className="details-inner">
You’ll need an HTTP endpoint that is available on the public internet. If you’re exploring this from your local computer you will need a way of exposing your network to the internet.

<aside>
ℹ️ An alternative to local development is to use an online code tool such as Replit or CodeSandbox. Both of these all you to develop these examples in an online editor and make the webserver available to the public internet. See this [CodeSandbox example](https://github.com/replicate/sandboxes/blob/main/curl/file-to-text/webhook.bash).

</aside>

The `ngrok` tool is a good example (`localtunnel` is another alternative):

```bash
npx ngrok http 3000
```

This will then give you a url such as [https://9c62-82-71-25-173.ngrok-free.app](https://9c62-82-71-25-173.ngrok-free.app/) that will forward all traffic to the application running at http://localhost:3000, such as your test application.

If you just want to see the webhook in action, you can use the `netcat` or `nc` tool to print activity to your command line.

```bash
while true; do printf 'HTTP/1.1 200 OK\r\n\r\n' | nc -l 3000; done
```

This will print all traffic to localhost:3000 to the command line and respond with a `200 OK` status code. Good enough for our example.

Now in a new terminal, build the request, providing the ngrok endpoint as the `"webhook"` input and `"completed"` to the `"webhook_events_filter"` :

<MDXSection show={props.usesVersionlessApi}>
```bash
output=$(
	curl --silent --show-error {{ predictionApiEndpoint }} \
		--request POST \
		--header "Authorization: Bearer $REPLICATE_API_TOKEN" \
		--header "Content-Type: application/json" \
		--data @- <<'EOM'
{
	"webhook": "https://9c62-82-71-25-173.ngrok-free.app",
	"webhook_events_filter": ["completed"],
	"input": {{ input }}
}
EOM
)
# { "id": "xyz...", "status": "starting", ... }
```
</MDXSection>
<MDXSection show={!props.usesVersionlessApi}>
```bash
output=$(
	curl --silent --show-error {{ predictionApiEndpoint }} \
		--request POST \
		--header "Authorization: Bearer $REPLICATE_API_TOKEN" \
		--header "Content-Type: application/json" \
		--data @- <<'EOM'
{
	"{{ runnableFieldName }}": "{{ runnableFieldValue }}",
	"webhook": "https://9c62-82-71-25-173.ngrok-free.app",
	"webhook_events_filter": ["completed"],
	"input": {{ input }}
}
EOM
)
# { "id": "xyz...", "status": "starting", ... }
```
</MDXSection>

The `netcat` tab should now start to output some information:

```bash
% while true; do printf 'HTTP/1.1 200 OK\r\n\r\n' | nc -l 3000; done
POST / HTTP/1.1
Host: 9c62-82-71-25-173.ngrok-free.app
User-Agent: Webhooks/1.20.0
Content-Length: 3597
Accept: */*
Content-Type: application/json
Webhook-Id: msg_2d5xSI7RjVVHdp2EL1b25sKgB03
Webhook-Signature: v1,GuLwfWKCxRt7vIovgd6z8Cmd07yAuRIUDra/C4bvuds=
Webhook-Timestamp: 1709311738
X-Forwarded-For: 52.24.126.164
X-Forwarded-Host: 9c62-82-71-25-173.ngrok-free.app
X-Forwarded-Proto: https
Accept-Encoding: gzip

{"completed_at":"2024-03-01T16:48:58.371165Z", ... }
```
</div>
</details>

## Cancel a prediction

Sometimes you want to cancel a prediction, perhaps the user has navigated away from the browser or canceled your application. To avoid costs for unused work you can use the `predictions.cancel` endpoint.

```bash
cancel_url=$(printf "%s" "$output" | jq -r .urls.cancel)

curl -s "$cancel_url" -H "Authorization: Bearer $REPLICATE_API_TOKEN"
```
